import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Message } from '../../types';
import { MessageRating } from './MessageRating';
import './ChatMessage.css';

interface ChatMessageProps {
  message: Message;
  userPhotoUrl?: string;
  messageIndex: number;
  conversationId: string;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ 
  message, 
  userPhotoUrl, 
  messageIndex, 
  conversationId 
}) => {
  const isUser = message.role === 'user';

  return (
    <div className={`chat-message ${isUser ? 'message-user' : 'message-assistant'}`}>
      <div className="message-avatar">
        {isUser ? (
          userPhotoUrl ? (
            <img src={userPhotoUrl} alt="You" className="avatar-image" />
          ) : (
            '👤'
          )
        ) : (
          '🤖'
        )}
      </div>
      <div className="message-content">
        <div className="message-header">
          <span className="message-role">{isUser ? 'You' : 'AI Assistant'}</span>
          <span className="message-time">
            {new Date(message.timestamp).toLocaleTimeString()}
          </span>
        </div>
        <div className="message-text">
          {isUser ? (
            // User messages render as plain text
            message.content
          ) : (
            // AI messages render with full markdown support
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
              components={{
                // Custom rendering for tables
                table: ({ node, ...props }) => (
                  <div className="markdown-table-wrapper">
                    <table {...props} />
                  </div>
                ),
                // Custom rendering for code blocks
                code: ({ node, className, children, ...props }: any) => {
                  const match = /language-(\w+)/.exec(className || '');
                  const inline = !className;
                  return !inline ? (
                    <div className="code-block">
                      <div className="code-block-header">
                        {match ? match[1] : 'code'}
                      </div>
                      <pre className={className}>
                        <code {...props}>{children}</code>
                      </pre>
                    </div>
                  ) : (
                    <code className="inline-code" {...props}>
                      {children}
                    </code>
                  );
                },
                // Custom rendering for headings with icons
                // eslint-disable-next-line jsx-a11y/heading-has-content
                h1: ({ node, ...props }) => (
                  <h1 className="markdown-h1" {...props} />
                ),
                // eslint-disable-next-line jsx-a11y/heading-has-content
                h2: ({ node, ...props }) => (
                  <h2 className="markdown-h2" {...props} />
                ),
                // eslint-disable-next-line jsx-a11y/heading-has-content
                h3: ({ node, ...props }) => (
                  <h3 className="markdown-h3" {...props} />
                ),
                // Custom rendering for lists
                ul: ({ node, ...props }) => (
                  <ul className="markdown-list" {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol className="markdown-list" {...props} />
                ),
                // Custom rendering for blockquotes
                blockquote: ({ node, ...props }) => (
                  <blockquote className="markdown-blockquote" {...props} />
                ),
                // Custom rendering for links
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                a: ({ node, ...props }) => (
                  <a className="markdown-link" target="_blank" rel="noopener noreferrer" {...props} />
                ),
              }}
            >
              {message.content
                .trim()
                .replace(/\n{2,}/g, '\n')  // Collapse ALL double newlines to single
                .replace(/(\*\*[^*]+\*\*:?)\n+/g, '$1\n')  // Normalize after bold headings
                .replace(/(#{1,3}[^\n]+)\n+/g, '$1\n')  // Normalize after # headings
              }
            </ReactMarkdown>
          )}
        </div>

        {/* Provider cards if present */}
        {!isUser && message.providers && message.providers.length > 0 && (
          <div className="provider-section">
            <div className="provider-title">Local healthcare providers</div>
            <div className="provider-cards">
              {message.providers.map((p, idx) => (
                <div key={idx} className="provider-card">
                  <div className="provider-name">{p.name}</div>
                  <div className="provider-meta">{p.specialty}{p.organization ? ` · ${p.organization}` : ''}</div>
                  {p.address && (
                    <div className="provider-meta">{p.address}{p.pincode ? `, ${p.pincode}` : ''}</div>
                  )}
                  {typeof p.distanceKm === 'number' && (
                    <div className="provider-meta">Approx. {p.distanceKm.toFixed(1)} km away</div>
                  )}
                  <div className="provider-actions">
                    {p.phone && (
                      <a className="provider-btn" href={`tel:${p.phone}`} onClick={(e)=>e.stopPropagation()}>Call</a>
                    )}
                    {p.website && (
                      <a className="provider-btn" href={p.website} target="_blank" rel="noreferrer" onClick={(e)=>e.stopPropagation()}>Website</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Rating Component for AI responses */}
        {!isUser && (
          <MessageRating
            messageIndex={messageIndex}
            conversationId={conversationId}
          />
        )}
      </div>
    </div>
  );
};
